import { type IProject } from '@featit/shared/types/index';
import {
  flattenNamespaceHierarchy,
  flattenWorkAreaHierarchy,
  type WorkAreaHierarchyRow,
} from '@featit/shared/work-area-hierarchy';
import type { INamespace } from '@/services/NamespaceApi';

/**
 * The sidebar's work-area list, grouped under the namespaces the work areas live in (I-0655,
 * AC-M12, AC-M13).
 *
 * Jay, 2026-09-24: *"namespaces are potentially available, but they must be mandatory!"* A namespace
 * panel above a flat list of work areas reads as "namespaces are optional". This is the tree the
 * list is drawn from instead: the namespace is the heading, and a work area only ever appears under
 * the namespace it belongs to.
 *
 * - The person's **own** namespace comes first, then every organisation by path, each followed
 *   depth-first by its subgroups.
 * - An organisation or subgroup with no work areas is still a group: it is where a new one is
 *   created. A namespace the tree does not list (somebody else's personal namespace holding a work
 *   area this person was added to) is a group too, headed by what the work area says about it.
 * - Within a group, work areas keep their own nesting and the person's saved order
 *   (`flattenWorkAreaHierarchy`).
 */

export interface IWorkAreaNamespaceGroup {
  /** The namespace id, or `path:<fullPath>` for one only known from its work areas. */
  key: string;
  namespaceId: string | null;
  /** The display name (never empty since I-0657), falling back to the handle. */
  name: string;
  /** The `/`-joined full path, which is what a qualified reference starts with. */
  fullPath: string;
  kind: 'personal' | 'organisation' | 'subgroup' | 'other';
  /** Subgroup nesting under its organisation; 0 for everything else. */
  depth: number;
  /** Whether this is the reader's own personal namespace. */
  own: boolean;
  /**
   * Whether the reader may create a subgroup or a work area directly here (I-1054, §10.13) —
   * `INamespace.canManageHere`, carried straight through. `false` for a namespace the tree does
   * not list at all (the `other` fallback below): there is no namespace row to ask the permission
   * of, the same reason `onCreateSubgroup`/`onCreateWorkArea` are already structurally absent for
   * it (`Layout.tsx`).
   */
  canManageHere: boolean;
  /** `INamespace.rolesHere`, for a refused create's reason (I-1679); absent for the fallback. */
  rolesHere?: readonly string[];
  rows: WorkAreaHierarchyRow<IProject>[];
}

function kindOf(namespace: INamespace): IWorkAreaNamespaceGroup['kind'] {
  if (namespace.kind === 'user') return 'personal';
  return namespace.parentId ? 'subgroup' : 'organisation';
}

export function groupWorkAreasByNamespace(
  projects: readonly IProject[],
  namespaces: readonly INamespace[] | null,
  workAreaOrder: string[] = [],
  /**
   * F-0380: the reader's own arrangement of the organisations and subgroups below their personal
   * namespace (which is always first and never reordered — there is only ever one). Reuses
   * `flattenWorkAreaHierarchy` for the namespace layer below, the same function that already orders
   * a work area among its siblings: a namespace's "position among its siblings" is the same
   * question, and a second ordering function here would have been a second answer to it.
   */
  namespaceOrder: string[] = [],
): IWorkAreaNamespaceGroup[] {
  const byNamespace = new Map<string, IProject[]>();
  const keyOf = (project: IProject): string =>
    project.namespaceId ?? `path:${project.namespace ?? ''}`;
  for (const project of projects) {
    const key = keyOf(project);
    byNamespace.set(key, [...(byNamespace.get(key) ?? []), project]);
  }

  const rowsFor = (key: string) =>
    flattenWorkAreaHierarchy(byNamespace.get(key) ?? [], workAreaOrder);

  const groups: IWorkAreaNamespaceGroup[] = [];
  const known = namespaces ?? [];

  const push = (namespace: INamespace, depth: number) => {
    groups.push({
      key: namespace.id,
      namespaceId: namespace.id,
      name: namespace.name || namespace.path,
      fullPath: namespace.fullPath,
      kind: kindOf(namespace),
      depth,
      own: namespace.kind === 'user',
      canManageHere: namespace.canManageHere,
      rolesHere: namespace.rolesHere,
      rows: rowsFor(namespace.id),
    });
    byNamespace.delete(namespace.id);
  };

  // The tree lists exactly one personal namespace: the reader's own (`namespaceTree`). Always
  // first, and never a candidate for `namespaceOrder` — there is only ever one of it.
  for (const personal of known.filter((namespace) => namespace.kind === 'user')) push(personal, 0);
  /*
    Every organisation and subgroup, depth-first, in the reader's saved arrangement. A subgroup
    whose organisation the tree does not hold is promoted to a root by `flattenWorkAreaHierarchy`
    itself (the same fallback a work area with an absent parent gets), so it still gets its own
    group rather than being dropped.
  */
  for (const row of flattenNamespaceHierarchy(known, namespaceOrder)) {
    push(row.project, row.depth);
  }

  // Work areas in namespaces the tree does not list: headed by what the work area knows.
  // The reader's own still comes first, which is what it is before the tree answers or when the
  // tree could not be read.
  const rest = [...byNamespace.entries()].sort(
    ([, left], [, right]) =>
      Number(right[0].namespaceIsOwn === true) - Number(left[0].namespaceIsOwn === true) ||
      (left[0].namespace ?? '').localeCompare(right[0].namespace ?? ''),
  );
  for (const [key, members] of rest) {
    const sample = members[0];
    const own = sample.namespaceIsOwn === true;
    groups.push({
      key,
      namespaceId: sample.namespaceId ?? null,
      name: sample.namespaceName || sample.namespace || 'Other work areas',
      fullPath: sample.namespace ?? '',
      kind: own ? 'personal' : 'other',
      depth: 0,
      own,
      canManageHere: false,
      rows: rowsFor(key),
    });
  }
  return groups;
}

/**
 * The groups the switcher shows: those holding a work area, **and every heading above one**
 * (I-0655, AC-M29). A picker leaves an empty namespace out, but a subgroup shown without its
 * organisation's heading reads as a namespace of its own, and `Platform` under nothing says less
 * than `Acme › Platform`. The groups are depth-first, so a group's descendants are the ones that
 * follow it at a greater depth.
 */
export function groupsWithWorkAreas(
  groups: readonly IWorkAreaNamespaceGroup[],
): IWorkAreaNamespaceGroup[] {
  return groups.filter((group, index) => {
    if (group.rows.length > 0) return true;
    for (let next = index + 1; next < groups.length; next += 1) {
      if (groups[next].depth <= group.depth) return false;
      if (groups[next].rows.length > 0) return true;
    }
    return false;
  });
}
